// src/components/account/DeleteAccountFlow.jsx
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Trash2,
  X,
} from "lucide-react";
import { deleteCurrentUserAccount } from "../../lib/account/accountDeletion";

const REASONS = [
  {
    value: "not_using_anymore",
    label: "I am not using Gwin anymore",
  },
  {
    value: "lessons_not_relevant",
    label: "The lessons are not relevant to me",
  },
  {
    value: "difficult_to_use",
    label: "The app is difficult to use",
  },
  {
    value: "technical_problems",
    label: "I experienced technical problems",
  },
  {
    value: "privacy_concerns",
    label: "I am concerned about my privacy or data",
  },
  {
    value: "paid_journey_too_expensive",
    label: "The paid journey is too expensive",
  },
  {
    value: "duplicate_account",
    label: "I created another account",
  },
  {
    value: "completed_what_i_needed",
    label: "I completed what I needed",
  },
  {
    value: "other",
    label: "Other",
  },
];

const RETURN_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "No" },
];

function normaliseText(value, maxLength) {
  return String(value || "").trim().slice(0, maxLength);
}

export default function DeleteAccountFlow({
  open,
  userEmail,
  onClose,
  onBeforeDelete,
  onDeleted,
}) {
  const [step, setStep] = useState(1);
  const [primaryReason, setPrimaryReason] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [experienceRating, setExperienceRating] = useState(null);
  const [wouldReturn, setWouldReturn] = useState(null);
  const [confirmationText, setConfirmationText] = useState("");
  const [confirmedIrreversible, setConfirmedIrreversible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const selectedReason = useMemo(
    () => REASONS.find((reason) => reason.value === primaryReason) || null,
    [primaryReason]
  );

  const detailsLabel = useMemo(() => {
    if (primaryReason === "technical_problems") {
      return "What problem did you experience?";
    }

    if (primaryReason === "privacy_concerns") {
      return "What privacy or data concern do you have?";
    }

    if (primaryReason === "difficult_to_use") {
      return "Which part of the app was difficult to use?";
    }

    if (primaryReason === "lessons_not_relevant") {
      return "What content would have been more relevant to you?";
    }

    if (primaryReason === "paid_journey_too_expensive") {
      return "What would have made the paid journey more suitable for you?";
    }

    if (primaryReason === "other") {
      return "Please tell us your reason";
    }

    return "Would you like to tell us more?";
  }, [primaryReason]);

  const requiresReasonDetails = primaryReason === "other";

  const canContinueFromReason =
    !!primaryReason &&
    (!requiresReasonDetails || normaliseText(reasonDetails, 1000).length > 0);

  const canDelete =
    confirmationText.trim().toUpperCase() === "DELETE" &&
    confirmedIrreversible &&
    !deleting;

  useEffect(() => {
    if (!open) return;

    setStep(1);
    setPrimaryReason("");
    setReasonDetails("");
    setAdditionalFeedback("");
    setExperienceRating(null);
    setWouldReturn(null);
    setConfirmationText("");
    setConfirmedIrreversible(false);
    setDeleting(false);
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !deleting) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, deleting, onClose]);

  if (!open) return null;

  const goNext = () => {
    setError("");

    if (step === 1 && !canContinueFromReason) {
      setError(
        requiresReasonDetails
          ? "Please enter your reason before continuing."
          : "Please choose a reason before continuing."
      );
      return;
    }

    setStep((current) => Math.min(3, current + 1));
  };

  const goBack = () => {
    if (deleting) return;

    setError("");
    setStep((current) => Math.max(1, current - 1));
  };

  const handleDelete = async () => {
    if (!canDelete) return;

    setDeleting(true);
    setError("");

    try {
      try {
        await onBeforeDelete?.();
      } catch (cleanupError) {
        console.warn(
          "Pre-deletion notification cleanup could not be completed:",
          cleanupError
        );
      }

      await deleteCurrentUserAccount({
        primaryReason,
        reasonDetails: normaliseText(reasonDetails, 1000),
        additionalFeedback: normaliseText(additionalFeedback, 2000),
        experienceRating,
        wouldReturn,
        confirmationText: confirmationText.trim(),
        confirmedIrreversible,
        metadata: {
          source: "profile_delete_account_flow",
          reason_label: selectedReason?.label || "",
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent : "",
          app_path:
            typeof window !== "undefined" ? window.location.pathname : "",
        },
      });

      await onDeleted?.();
    } catch (deleteError) {
      console.error("Account deletion failed:", deleteError);
      setError(
        deleteError?.message ||
          "We could not delete your account. Please try again."
      );
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/55 px-0 sm:items-center sm:px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      <div className="max-h-[94svh] w-full overflow-y-auto rounded-t-[28px] bg-white shadow-2xl sm:max-w-md sm:rounded-[28px]">
        <div className="sticky top-0 z-10 border-b border-[#EFEFEF] bg-white px-5 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">
                Delete account
              </p>

              <h2
                id="delete-account-title"
                className="mt-1 text-xl font-bold text-[#333333]"
              >
                {step === 1 && "Why are you leaving?"}
                {step === 2 && "Tell us about your experience"}
                {step === 3 && "Permanently delete your account"}
              </h2>
            </div>

            <button
              type="button"
              onClick={() => onClose?.()}
              disabled={deleting}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F3F3F3] text-[#555555] hover:bg-[#E8E8E8] disabled:opacity-50"
              aria-label="Close account deletion"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            {[1, 2, 3].map((number) => (
              <div
                key={number}
                className={`h-1.5 rounded-full ${
                  number <= step ? "bg-red-500" : "bg-[#E8E8E8]"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="px-5 py-5 sm:px-6 sm:py-6">
          {error && (
            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-700">
              {error}
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="text-sm leading-relaxed text-[#757575]">
                Your answer helps us understand what Gwin should improve. Choose
                the main reason you want to delete your account.
              </p>

              <div className="mt-5 space-y-2.5">
                {REASONS.map((reason) => {
                  const selected = primaryReason === reason.value;

                  return (
                    <button
                      key={reason.value}
                      type="button"
                      onClick={() => {
                        setPrimaryReason(reason.value);
                        setError("");
                      }}
                      className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                        selected
                          ? "border-red-400 bg-red-50 text-red-800"
                          : "border-[#E5E5E5] bg-white text-[#333333] hover:bg-[#FAFAFA]"
                      }`}
                    >
                      <span>{reason.label}</span>

                      {selected && (
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-red-600" />
                      )}
                    </button>
                  );
                })}
              </div>

              {!!primaryReason && (
                <div className="mt-5">
                  <label className="mb-2 block text-[12px] font-bold uppercase tracking-wider text-[#757575]">
                    {detailsLabel}
                    {requiresReasonDetails ? " *" : ""}
                  </label>

                  <textarea
                    value={reasonDetails}
                    onChange={(event) =>
                      setReasonDetails(event.target.value.slice(0, 1000))
                    }
                    rows={4}
                    placeholder="Share any details that may help us understand."
                    className="w-full resize-none rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm text-[#333333] outline-none placeholder:text-[#BCBCBC] focus:border-red-400 focus:ring-4 focus:ring-red-100"
                  />

                  <p className="mt-1 text-right text-[11px] text-[#9A9A9A]">
                    {reasonDetails.length}/1000
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-[12px] font-bold uppercase tracking-wider text-[#757575]">
                  What could we have done better?
                </label>

                <textarea
                  value={additionalFeedback}
                  onChange={(event) =>
                    setAdditionalFeedback(event.target.value.slice(0, 2000))
                  }
                  rows={5}
                  placeholder="This is optional, but your feedback can help us improve Gwin."
                  className="w-full resize-none rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm text-[#333333] outline-none placeholder:text-[#BCBCBC] focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
                />

                <p className="mt-1 text-right text-[11px] text-[#9A9A9A]">
                  {additionalFeedback.length}/2000
                </p>
              </div>

              <div>
                <p className="text-[12px] font-bold uppercase tracking-wider text-[#757575]">
                  How would you rate your overall experience?
                </p>

                <div className="mt-3 grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setExperienceRating(rating)}
                      className={`flex h-11 items-center justify-center rounded-xl border text-sm font-bold transition ${
                        experienceRating === rating
                          ? "border-[#FF914D] bg-[#FFF4EC] text-[#E9652D]"
                          : "border-[#E5E5E5] bg-white text-[#555555] hover:bg-[#FAFAFA]"
                      }`}
                      aria-label={`Rate ${rating} out of 5`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>

                <div className="mt-1 flex justify-between text-[10px] text-[#9A9A9A]">
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>

              <div>
                <p className="text-[12px] font-bold uppercase tracking-wider text-[#757575]">
                  Would you consider using Gwin again?
                </p>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  {RETURN_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setWouldReturn(option.value)}
                      className={`rounded-xl border px-3 py-3 text-sm font-bold transition ${
                        wouldReturn === option.value
                          ? "border-[#FF914D] bg-[#FFF4EC] text-[#E9652D]"
                          : "border-[#E5E5E5] bg-white text-[#555555] hover:bg-[#FAFAFA]"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">
                    <AlertTriangle className="h-5 w-5" />
                  </div>

                  <div>
                    <p className="text-sm font-bold text-red-900">
                      This action cannot be undone
                    </p>

                    <p className="mt-1 text-xs leading-relaxed text-red-800">
                      Your account, profile information, learning progress, XP,
                      practical challenge data, achievements, referral progress,
                      and app access will be permanently removed.
                    </p>
                  </div>
                </div>
              </div>

              {userEmail && (
                <div className="mt-4 rounded-2xl bg-[#FAFAFA] px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#757575]">
                    Account being deleted
                  </p>
                  <p className="mt-1 break-all text-sm font-semibold text-[#333333]">
                    {userEmail}
                  </p>
                </div>
              )}

              <div className="mt-5">
                <label className="mb-2 block text-[12px] font-bold uppercase tracking-wider text-[#757575]">
                  Type DELETE to confirm
                </label>

                <input
                  type="text"
                  value={confirmationText}
                  onChange={(event) => setConfirmationText(event.target.value)}
                  autoComplete="off"
                  placeholder="DELETE"
                  className="w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-bold tracking-wide text-[#333333] outline-none placeholder:text-[#BCBCBC] focus:border-red-400 focus:ring-4 focus:ring-red-100"
                />
              </div>

              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl border border-[#E5E5E5] bg-white px-4 py-4">
                <input
                  type="checkbox"
                  checked={confirmedIrreversible}
                  onChange={(event) =>
                    setConfirmedIrreversible(event.target.checked)
                  }
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />

                <span className="text-sm leading-relaxed text-[#555555]">
                  I understand that deleting my account is permanent and cannot
                  be undone.
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-[#EFEFEF] bg-white px-5 py-4 sm:px-6">
          <div className="flex gap-3">
            {step > 1 && (
              <button
                type="button"
                onClick={goBack}
                disabled={deleting}
                className="inline-flex h-12 flex-1 items-center justify-center rounded-2xl border border-[#E5E5E5] bg-white px-4 text-sm font-bold text-[#333333] hover:bg-[#FAFAFA] disabled:opacity-50"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </button>
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={step === 1 && !canContinueFromReason}
                className="h-12 flex-1 rounded-2xl bg-[#FF914D] px-4 text-sm font-bold text-white hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDelete}
                disabled={!canDelete}
                className="inline-flex h-12 flex-1 items-center justify-center rounded-2xl bg-red-600 px-4 text-sm font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting account…
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Permanently delete
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}